import { useEffect } from 'react'
import { MyButton } from '@interstellar-component'
import { useApp } from '@src/AppContext'
import { Access } from '@src/services/Helper'
import { useSettings } from '../Context'

function StatusBadge({ status }) {
  if (!status) return null
  if (status.captcha_required_at) {
    return (
      <div className="rounded-md border border-[#FECDCA] bg-[#FEF3F2] p-3 text-sm">
        <div className="font-semibold text-[#B42318]">CAPTCHA required — upstream auth blocked</div>
        <p className="mt-1 text-[#912018]">
          GetContact has flagged the account. Solve the CAPTCHA inside the GetContact 6.0.0 app on the
          rooted Android, extract the new <code>CHAT_TOKEN</code> / <code>FINAL_KEY</code> from{' '}
          <code>GetContactSettingsPref.xml</code>, and save them below.
        </p>
        {status.captcha_message && (
          <p className="mt-1 text-xs text-[#912018]">Upstream message: {status.captcha_message}</p>
        )}
        <p className="mt-1 text-xs text-[#912018]">
          Flagged at: {new Date(status.captcha_required_at).toLocaleString()}
        </p>
      </div>
    )
  }
  if (!status.is_active) {
    return (
      <div className="rounded-md border border-[#FEDF89] bg-[#FFFAEB] p-3 text-sm">
        <div className="font-semibold text-[#93370D]">Inactive — lookups are paused</div>
        <p className="mt-1 text-[#7A2E0E]">
          Save fresh credentials below to reactivate.
        </p>
      </div>
    )
  }
  return (
    <div className="rounded-md border border-[#ABEFC6] bg-[#ECFDF3] p-3 text-sm">
      <div className="font-semibold text-[#067647]">Active</div>
      <p className="mt-1 text-[#085D3A]">
        {status.last_successful_call_at
          ? `Last successful upstream call at ${new Date(status.last_successful_call_at).toLocaleString()}.`
          : 'No successful upstream call recorded yet.'}
      </p>
    </div>
  )
}

export default function GetContactSettings() {
  const { hasPermission } = useApp()
  const {
    getcontactStatus,
    getcontactToken,
    setGetcontactToken,
    getcontactFinalKey,
    setGetcontactFinalKey,
    getcontactClientDeviceId,
    setGetcontactClientDeviceId,
    getcontactAppVersion,
    setGetcontactAppVersion,
    isLoadingGetcontact,
    isSavingGetcontact,
    fetchGetcontactSettings,
    updateGetcontactSettings,
    cancelGetcontactSettings,
  } = useSettings()

  const canView = hasPermission(Access.GETCONTACT_SETTING, 'view')
  const canEdit = hasPermission(Access.GETCONTACT_SETTING, 'edit')

  useEffect(() => {
    if (canView) fetchGetcontactSettings()
  }, [canView, fetchGetcontactSettings])

  if (isLoadingGetcontact) {
    return (
      <div className="flex items-center justify-center pt-16">
        <span className="text-sm text-[#535862]">Loading...</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col pt-8">
      {/* Section header */}
      <div className="flex flex-col gap-1 w-full pb-5 border-b border-[#e9eaeb]">
        <h2 className="text-lg font-semibold text-[#181d27]">GetContact Credential</h2>
        <p className="text-sm text-[#535862]">
          Manage the upstream GetContact token and final key used by phone enrichment lookups. Tokens
          rotate when the source app refreshes — re-pull from the rooted Android app whenever the
          server returns an auth error.
        </p>
      </div>

      {/* Status banner */}
      <div className="py-5 border-b border-[#e9eaeb]">
        <StatusBadge status={getcontactStatus} />
        {getcontactStatus && (
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <dt className="text-[#475467]">Current masked token</dt>
            <dd className="text-[#181d27] font-mono">{getcontactStatus.token || '—'}</dd>
            <dt className="text-[#475467]">Final key set</dt>
            <dd className="text-[#181d27]">{getcontactStatus.final_key_set ? 'Yes' : 'No'}</dd>
            <dt className="text-[#475467]">Last updated</dt>
            <dd className="text-[#181d27]">
              {getcontactStatus.updated_at ? new Date(getcontactStatus.updated_at).toLocaleString() : '—'}
              {getcontactStatus.updater?.name ? ` by ${getcontactStatus.updater.name}` : ''}
            </dd>
          </dl>
        )}
      </div>

      {/* Token */}
      <div className="flex flex-row w-full py-5 border-b border-[#e9eaeb] items-start">
        <div className="w-[300px] shrink-0 mr-8 flex flex-col gap-1">
          <label className="text-sm font-semibold text-[#344054]">
            Token <span className="text-[#14378E]">*</span>
          </label>
          <p className="text-sm text-[#475467]">
            Value of <code>CHAT_TOKEN</code> from <code>GetContactSettingsPref.xml</code>. FE-encrypted
            before being sent to the server; never displayed back in plaintext.
          </p>
        </div>
        <div className="flex-1 max-w-[520px]">
          <div className="relative flex items-center w-full rounded-lg border border-[#D0D5DD] bg-white shadow-sm overflow-hidden focus-within:ring-1 focus-within:ring-[#14378E] focus-within:border-[#14378E]">
            <input
              type="password"
              autoComplete="off"
              spellCheck={false}
              disabled={!canEdit}
              className="block w-full border-0 py-2.5 px-3.5 text-gray-900 ring-0 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 outline-none disabled:bg-gray-50"
              placeholder="dcslgt56005bf3fcd…"
              value={getcontactToken}
              onChange={(e) => setGetcontactToken(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Final Key */}
      <div className="flex flex-row w-full py-5 border-b border-[#e9eaeb] items-start">
        <div className="w-[300px] shrink-0 mr-8 flex flex-col gap-1">
          <label className="text-sm font-semibold text-[#344054]">
            Final Key <span className="text-[#14378E]">*</span>
          </label>
          <p className="text-sm text-[#475467]">
            Value of <code>FINAL_KEY</code> from the same shared-prefs file. Must be 32, 48, or 64 hex
            characters (AES-128/192/256).
          </p>
        </div>
        <div className="flex-1 max-w-[520px]">
          <div className="relative flex items-center w-full rounded-lg border border-[#D0D5DD] bg-white shadow-sm overflow-hidden focus-within:ring-1 focus-within:ring-[#14378E] focus-within:border-[#14378E]">
            <input
              type="password"
              autoComplete="off"
              spellCheck={false}
              disabled={!canEdit}
              className="block w-full border-0 py-2.5 px-3.5 text-gray-900 ring-0 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 outline-none disabled:bg-gray-50"
              placeholder="8fc3547d03716fba…"
              value={getcontactFinalKey}
              onChange={(e) => setGetcontactFinalKey(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Client Device ID */}
      <div className="flex flex-row w-full py-5 border-b border-[#e9eaeb] items-start">
        <div className="w-[300px] shrink-0 mr-8 flex flex-col gap-1">
          <label className="text-sm font-semibold text-[#344054]">Client Device ID</label>
          <p className="text-sm text-[#475467]">
            Android-ID of the rooted device the token was extracted from. Sent in the
            <code> X-Client-Device-Id </code> header.
          </p>
        </div>
        <div className="flex-1 max-w-[520px]">
          <div className="relative flex items-center w-full rounded-lg border border-[#D0D5DD] bg-white shadow-sm overflow-hidden focus-within:ring-1 focus-within:ring-[#14378E] focus-within:border-[#14378E]">
            <input
              type="text"
              autoComplete="off"
              spellCheck={false}
              disabled={!canEdit}
              className="block w-full border-0 py-2.5 px-3.5 text-gray-900 ring-0 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 outline-none disabled:bg-gray-50"
              placeholder="68cc13eda0a85b51"
              value={getcontactClientDeviceId}
              onChange={(e) => setGetcontactClientDeviceId(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* App Version */}
      <div className="flex flex-row w-full py-5 border-b border-[#e9eaeb] items-start">
        <div className="w-[300px] shrink-0 mr-8 flex flex-col gap-1">
          <label className="text-sm font-semibold text-[#344054]">App Version</label>
          <p className="text-sm text-[#475467]">
            API protocol version sent in <code>X-App-Version</code>. Must be <code>5.6.2</code> for the <code>/v2.8/*</code> endpoints; other values (e.g. <code>6.0.0</code>) return <code>403003 Authentication failed</code>.
          </p>
        </div>
        <div className="flex-1 max-w-[180px]">
          <div className="relative flex items-center w-full rounded-lg border border-[#D0D5DD] bg-white shadow-sm overflow-hidden focus-within:ring-1 focus-within:ring-[#14378E] focus-within:border-[#14378E]">
            <input
              type="text"
              autoComplete="off"
              spellCheck={false}
              disabled={!canEdit}
              className="block w-full border-0 py-2.5 px-3.5 text-gray-900 ring-0 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 outline-none disabled:bg-gray-50"
              placeholder="5.6.2"
              value={getcontactAppVersion}
              onChange={(e) => setGetcontactAppVersion(e.target.value)}
            />
          </div>
        </div>
      </div>

      {canEdit && (
        <div className="flex w-full justify-end gap-3 pt-6 pb-2">
          <MyButton
            color="secondary"
            size="md"
            variant="outlined"
            onClick={cancelGetcontactSettings}
            disabled={isSavingGetcontact}
          >
            Cancel
          </MyButton>
          <MyButton
            color="primary"
            size="md"
            variant="filled"
            onClick={updateGetcontactSettings}
            disabled={isSavingGetcontact}
          >
            {isSavingGetcontact ? 'Saving…' : 'Save'}
          </MyButton>
        </div>
      )}
    </div>
  )
}
